import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../WriteReviewModal/WriteReviewModal.css';

/**
 * EditReviewModal - Modal for editing existing event reviews
 *
 * Only allows editing:
 * - Title
 * - Description text
 * - Organizer recommendations
 *
 * Ratings, role, and cultural discussions are locked to preserve authenticity.
 */
const EditReviewModal = ({
  isOpen,
  onClose,
  review,
  onSuccess,
  apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'
}) => {
  // Editable fields
  const [title, setTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [organizerRecommendations, setOrganizerRecommendations] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const modalRef = useRef(null);

  // Swear words filter list
  const swearWords = ['fuck', 'shit', 'ass', 'bitch', 'damn', 'crap', 'bastard', 'dick', 'cock', 'pussy', 'cunt', 'whore', 'slut', 'nigger', 'faggot', 'retard'];

  // Populate form with existing review data when modal opens
  useEffect(() => {
    if (isOpen && review) {
      setTitle(review.title || '');
      setReviewText(review.description || '');
      setOrganizerRecommendations(review.organizerRecommendations || '');
      setShowSuccess(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, review]);

  // Handle click outside modal to close
  const handleOverlayClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  // Calculate word count
  const getWordCount = useCallback((text) => {
    const trimmed = text.trim();
    if (!trimmed) return 0;
    return trimmed.split(/\s+/).filter(word => word.length > 0).length;
  }, []);

  const wordCount = getWordCount(reviewText);

  // Get word count class for styling
  const getWordCountClass = () => {
    if (wordCount < 20) return 'warning';
    if (wordCount > 100) return 'error';
    return 'valid';
  };

  // Check for swear words
  const containsSwearWords = (text) => {
    const lowerText = text.toLowerCase();
    return swearWords.some(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'i');
      return regex.test(lowerText);
    });
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validation
    if (!reviewText.trim()) {
      alert('Please write your review');
      return;
    }

    if (wordCount < 20) {
      alert(`Your review is too short. Please write at least 20 words. (Current: ${wordCount} words)`);
      return;
    }

    if (wordCount > 100) {
      alert(`Your review is too long. Please keep it under 100 words. (Current: ${wordCount} words)`);
      return;
    }

    if (containsSwearWords(reviewText)) {
      alert('Please keep your review respectful and avoid inappropriate language.');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBase}/events/feedback/${review._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: title.trim() || undefined,
          description: reviewText.trim(),
          organizerRecommendations: organizerRecommendations.trim() || undefined
        })
      });

      const data = await response.json();

      if (data.success) {
        setShowSuccess(true);
        setTimeout(() => {
          onClose();
          onSuccess?.();
        }, 2000);
      } else {
        alert(data.error || data.message || 'Failed to update review');
      }
    } catch (error) {
      console.error('Error updating review:', error);
      alert('Failed to update review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const eventTitle = review?.eventId?.title || 'Event';

  return (
    <div
      className={`review-modal-overlay ${isOpen ? 'show' : ''}`}
      ref={modalRef}
      onClick={handleOverlayClick}
    >
      <div className="review-modal">
        <div className="review-modal-header">
          <h3><i className="bi bi-pencil-square"></i> Edit Review</h3>
          <button className="review-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="review-modal-body">
          {showSuccess ? (
            <div className="review-success-message">
              <i className="bi bi-check-circle-fill"></i>
              <h4>Your review has been updated!</h4>
              <p>Changes saved successfully.</p>
            </div>
          ) : (
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Event Title Display */}
            <div className="form-group">
              <label>Editing review for:</label>
              <div style={{ fontWeight: 600, color: 'var(--purple-accent)' }}>{eventTitle}</div>
            </div>

            {/* Info about what can be edited */}
            <div className="form-group">
              <div style={{
                background: 'var(--purple-light, #E8DFF0)',
                padding: '0.75rem 1rem',
                borderRadius: '8px',
                fontSize: '0.85rem',
                color: 'var(--text-muted, #666)'
              }}>
                <i className="bi bi-info-circle" style={{ marginRight: '0.5rem' }}></i>
                You can edit your title, review text, and organizer recommendations. Ratings and role cannot be changed to preserve authenticity.
              </div>
            </div>

            {/* Title */}
            <div className="form-group">
              <label>Title <small>(optional)</small></label>
              <input
                type="text"
                className="form-control-review"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Give your review a catchy title..."
              />
            </div>

            {/* Review Text */}
            <div className="form-group">
              <label>
                Your Review <span className="required-asterisk">*</span>{' '}
                <small style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(20-100 words)</small>
              </label>
              <textarea
                className="form-control-review"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                rows="5"
                placeholder="Share your experience to help others decide..."
                required
              />
              <div className={`word-count-display ${getWordCountClass()}`}>
                {wordCount} / 100 words
              </div>
            </div>

            {/* Organizer Recommendations */}
            <div className="form-group">
              <div className="organizer-recommendations">
                <label>Recommendations for organizers <small>(optional)</small></label>
                <textarea
                  className="form-control-review"
                  value={organizerRecommendations}
                  onChange={(e) => setOrganizerRecommendations(e.target.value)}
                  rows="3"
                  placeholder="Private suggestions for the organizers..."
                />
                <p className="hint"><i className="bi bi-lock"></i> Private - only shared with organizers</p>
              </div>
            </div>
          </form>
          )}
        </div>

        {!showSuccess && (
        <div className="review-modal-footer">
          <button className="btn-cancel-review" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-submit-review"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="bi bi-hourglass"></i> Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-lg"></i> Save Changes
              </>
            )}
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default EditReviewModal;
